import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import LoadingButton from '../../../elements/common/LoadingButton';
import FormFileGroup from '../../../elements/formElements/FormFileGroup';
import axios from '../../../services/MyAxios';

import { CASINO_GAME_CREATORS_SHOW } from '../Helpers/CrmUrls';
import { replaceUrlParams } from '../Helpers/UrlHelper';

import CasinoCRMApi from 'casino/Api/CRM/CasinoCRMApi';
import Loader from 'crm/elements/Loader';
import SweetAlert from 'crm/elements/alerts/SweetAlert';
import Card from 'crm/elements/common/Card';
import AdvancedSelect from 'crm/elements/formElements/AdvancedSelect';
import handleValidationErrors from 'crm/helpers/ResponseErrorHelper';
import useNumberParams from 'crm/hooks/useNumberParams';

function GameCreatorGameEdit() {
    const [game, setGame] = useState({
        name: '',
        image_path: '',
        casino_game_seo_tag_id: 0,
        image: File,
    });

    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [seoMetaTitles, setSeoMetaTitles] = useState([]);
    const [fileInputKey, setFileInputKey] = useState(Date.now());
    const { gameCreatorId, gameId } = useNumberParams();

    const baseApiUrl = `/crea/casino/api/game/${gameId}`;

    const getGameApiUrl = baseApiUrl;
    const updateImageApiUrl = `${baseApiUrl}/update-image`;
    const resetImageApiUrl = `${baseApiUrl}/reset-image`;

    const history = useHistory();

    async function loadState() {
        getGameCreatorGameEdit();
        setSeoMetaTitles(await CasinoCRMApi.seoSettingsApi.getTitlesByCreatorId(gameCreatorId));
    }

    useEffect(() => {
        setIsLoading(true);
        loadState();
        setIsLoading(false);
    }, []);

    function getGameCreatorGameEdit() {
        axios
            .get(getGameApiUrl)
            .then((response) => {
                setGame(response.data);
            })
            .catch(() => {
                SweetAlert.error('Cannot retrieve game info from server').then(() => {
                    history.push({
                        pathname: replaceUrlParams(CASINO_GAME_CREATORS_SHOW, { id: gameCreatorId }),
                    });
                });
            });
    }

    function handleUpdateImage() {
        if (!game.image) {
            SweetAlert.error('Set image before submitting');
            return;
        }

        axios
            .post(updateImageApiUrl, game, {
                headers: {
                    Accept: 'application/json',
                    'content-type': 'multipart/form-data',
                },
            })
            .then(() => {
                SweetAlert.success('Image has been updated');
                getGameCreatorGameEdit();
                setFileInputKey(Date.now());
            })
            .catch((error) => {
                handleValidationErrors(error, setErrors);
            });
    }

    function handleResetImage() {
        axios
            .post(resetImageApiUrl)
            .then(() => {
                SweetAlert.success('Image has been reset');
                getGameCreatorGameEdit();
                setFileInputKey(Date.now());
            })
            .catch(() => {
                SweetAlert.error('Cannot reset game image');
            });
    }

    async function handleImageChange(event) {
        const selectedFile = event.target.files[0];

        setGame({
            ...game,
            image: selectedFile,
        });
    }

    function getErrorsFor(field) {
        return errors?.[field]?.[0];
    }

    const handleSeoTemplateChange = () => {
        CasinoCRMApi.games
            .changeSeoTag({
                id: gameId,
                casino_game_seo_tag_id: game.casino_game_seo_tag_id,
            })
            .then(() => {
                SweetAlert.success('Seo tag updated successfully');
                getGameCreatorGameEdit();
            })
            .catch((error) => {
                handleValidationErrors(error, setErrors);
            });
    };

    const getContent = () => {
        return (
            <Card title={game.name}>
                <form>
                    <div style={{ maxWidth: '400px', height: 'auto' }}>
                        <FormFileGroup
                            key={fileInputKey}
                            field="image"
                            value={game.image_path}
                            label="Game image"
                            error={getErrorsFor('image')}
                            handleChange={(event) => handleImageChange(event)}
                        />
                    </div>
                    <LoadingButton type="button" className="btn btn-secondary mr-2" onClick={() => handleResetImage()}>
                        Reset image
                    </LoadingButton>
                    <LoadingButton type="button" className="btn btn-primary" onClick={() => handleUpdateImage()}>
                        Update image
                    </LoadingButton>
                    <hr />
                    {/* display only for games with available seo templates */}
                    {seoMetaTitles.length > 0 && (
                        <div>
                            <AdvancedSelect
                                field="casino_game_seo_tag_id"
                                value={game.casino_game_seo_tag_id}
                                label="SEO template"
                                error={(getErrorsFor('id') || getErrorsFor('casino_game_seo_tag_id')) ?? undefined}
                                handleChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                    const selectedValue = event.target.value;
                                    setGame({
                                        ...game,
                                        casino_game_seo_tag_id: parseInt(selectedValue),
                                    });
                                }}
                                options={seoMetaTitles}
                                optionAccessor="content"
                                optionValueAccessor="id"
                                required
                            />
                            <LoadingButton
                                type="button"
                                className="btn btn-primary"
                                onClick={() => handleSeoTemplateChange()}
                            >
                                Update
                            </LoadingButton>
                        </div>
                    )}
                </form>
            </Card>
        );
    };

    return <Loader loading={isLoading} content={getContent} />;
}

export default GameCreatorGameEdit;
